import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

type PdfProfile = 'download' | 'email';

export interface PdfBuildOptions {
  profile?: PdfProfile;
}

type ImageType = 'PNG' | 'JPEG';
type ImageCompression = 'NONE' | 'FAST' | 'MEDIUM' | 'SLOW';

type ResolvedPdfBuildOptions = {
  scale: number;
  imageType: ImageType;
  jpegQuality: number;
  imageCompression: ImageCompression;
  maxCanvasPixels: number;
  maxCanvasDimension: number;
  pdfCompress: boolean;
};

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

function resolvePdfBuildOptions(options?: PdfBuildOptions, pageHint = 1): ResolvedPdfBuildOptions {
  const profile = options?.profile || 'download';
  const pageCount = Math.max(1, pageHint || 1);

  if (profile === 'email') {
    const extraPages = Math.max(0, pageCount - 1);
    const scale = clamp(1.45 - extraPages * 0.06, 1.1, 1.45);
    const jpegQuality = clamp(0.88 - extraPages * 0.03, 0.72, 0.88);
    const maxCanvasPixels = Math.round(clamp(2_400_000 - extraPages * 220_000, 1_300_000, 2_400_000));
    const maxCanvasDimension = Math.round(clamp(2200 - extraPages * 140, 1400, 2200));

    return {
      scale,
      imageType: 'JPEG',
      jpegQuality,
      imageCompression: 'MEDIUM',
      maxCanvasPixels,
      maxCanvasDimension,
      pdfCompress: true,
    };
  }

  return {
    scale: 2,
    imageType: 'PNG',
    jpegQuality: 0.92,
    imageCompression: 'NONE',
    maxCanvasPixels: 5_000_000,
    maxCanvasDimension: 3200,
    pdfCompress: true,
  };
}

function renderToCanvas(node: HTMLElement, scale: number): Promise<HTMLCanvasElement> {
  return html2canvas(node, {
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    scale,
    logging: false,
    removeContainer: true,
    imageTimeout: 15000,
  } as any);
}

function downscaleCanvasIfNeeded(
  sourceCanvas: HTMLCanvasElement,
  maxPixels: number,
  maxDimension: number,
): HTMLCanvasElement {
  const width = Math.max(1, sourceCanvas.width || 1);
  const height = Math.max(1, sourceCanvas.height || 1);
  const area = width * height;
  const longest = Math.max(width, height);

  let ratio = 1;
  if (area > maxPixels) ratio = Math.min(ratio, Math.sqrt(maxPixels / area));
  if (longest > maxDimension) ratio = Math.min(ratio, maxDimension / longest);

  if (!(ratio < 0.999)) return sourceCanvas;

  const targetWidth = Math.max(1, Math.round(width * ratio));
  const targetHeight = Math.max(1, Math.round(height * ratio));
  const resized = document.createElement('canvas');
  resized.width = targetWidth;
  resized.height = targetHeight;
  const ctx = resized.getContext('2d');
  if (!ctx) return sourceCanvas;

  // White background avoids large alpha payloads and improves JPEG output.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, targetWidth, targetHeight);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(sourceCanvas, 0, 0, width, height, 0, 0, targetWidth, targetHeight);

  return resized;
}

function canvasToImageDataUrl(canvas: HTMLCanvasElement, opts: ResolvedPdfBuildOptions): { dataUrl: string; format: ImageType } {
  if (opts.imageType === 'PNG') {
    return { dataUrl: canvas.toDataURL('image/png'), format: 'PNG' };
  }
  return {
    dataUrl: canvas.toDataURL('image/jpeg', opts.jpegQuality),
    format: 'JPEG',
  };
}

function addCanvasToPdf(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  x: number,
  y: number,
  width: number,
  height: number,
  opts: ResolvedPdfBuildOptions,
) {
  const renderCanvas = downscaleCanvasIfNeeded(canvas, opts.maxCanvasPixels, opts.maxCanvasDimension);
  const { dataUrl, format } = canvasToImageDataUrl(renderCanvas, opts);
  (pdf as any).addImage(dataUrl, format, x, y, width, height, undefined, opts.imageCompression);

  if (renderCanvas !== canvas) {
    renderCanvas.width = 1;
    renderCanvas.height = 1;
  }
}

function createPdf(opts: ResolvedPdfBuildOptions): jsPDF {
  return new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: opts.pdfCompress,
  } as any);
}

export async function buildPdfFromElement(element: HTMLElement, options?: PdfBuildOptions): Promise<jsPDF> {
  const pageNodes = Array.from(element.querySelectorAll<HTMLElement>('[data-pdf-page]'));
  const opts = resolvePdfBuildOptions(options, pageNodes.length || 1);
  const pdf = createPdf(opts);
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  // If we have explicit pages, render each page individually.
  if (pageNodes.length > 0) {
    for (let i = 0; i < pageNodes.length; i++) {
      const pageEl = pageNodes[i];
      const canvas = await renderToCanvas(pageEl, opts.scale);

      if (i > 0) pdf.addPage();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const renderWidth = imgWidth * ratio;
      const renderHeight = imgHeight * ratio;
      const x = (pdfWidth - renderWidth) / 2;
      const y = (pdfHeight - renderHeight) / 2;

      addCanvasToPdf(pdf, canvas, x, y, renderWidth, renderHeight, opts);
    }

    return pdf;
  }

  // Fallback: render the full element and slice by A4 height.
  const canvas = await renderToCanvas(element, opts.scale);

  const imgWidth = canvas.width;
  const imgHeight = canvas.height;
  const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
  const scaledHeight = imgHeight * ratio;

  let position = 0;
  let pageCount = 0;

  while (position < scaledHeight) {
    if (pageCount > 0) pdf.addPage();

    const sourceY = Math.max(0, Math.floor(position / ratio));
    const clipHeight = Math.max(1, Math.floor(Math.min(pdfHeight / ratio, imgHeight - sourceY)));

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = imgWidth;
    tempCanvas.height = clipHeight;

    const ctx = tempCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, imgWidth, clipHeight);
      ctx.drawImage(canvas, 0, sourceY, imgWidth, clipHeight, 0, 0, imgWidth, clipHeight);
      const renderHeight = clipHeight * ratio;
      addCanvasToPdf(pdf, tempCanvas, 0, 0, pdfWidth, renderHeight, opts);
      position += renderHeight;
    }

    tempCanvas.width = 1;
    tempCanvas.height = 1;
    pageCount++;
  }

  return pdf;
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return window.btoa(binary);
}

export async function buildPdfBase64FromElement(element: HTMLElement, options?: PdfBuildOptions): Promise<string> {
  const pdf = await buildPdfFromElement(element, options);
  const arrayBuffer = pdf.output('arraybuffer') as ArrayBuffer;
  return arrayBufferToBase64(arrayBuffer);
}
